import React from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Badge from "components/ui/badge/Badge";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { useDialogManager } from "lib/providers/DialogManager";
import PaywallCTAModal from "./PaywallCTAModal";

export type SubscriptionAddonCardProps = {
  dataType: "STRATEGY" | "BIDS";
  icon: IconProps["icon"];
  title: string;
  description: string;
  oldPrice: string;
  newPrice: string;
};

export default function SubscriptionAddonCard({
  icon,
  dataType,
  title,
  description,
  oldPrice,
  newPrice,
}: SubscriptionAddonCardProps): JSX.Element {
  const { mutate: actionPql } = usePqlActioned();
  const dialogManager = useDialogManager();

  const handleRequestInformation = () => {
    actionPql({
      actionType: "Book a demo in-app form submission",
      dataType,
      pageUrl: window.location.href,
      contextSource: "Book a demo form",
    });

    void dialogManager.openDialog(PaywallCTAModal, {
      showBookADemoForm: true,
      featureType: dataType,
    });
  };

  return (
    <Card>
      <Flex column gap={24}>
        <Section>
          <Flex alignItems="center" gap={8}>
            <UIcon icon={icon} size={24} />
            <Text h2>{title}</Text>
          </Flex>
        </Section>
        <Section>
          <Flex alignItems="center" gap={16}>
            <OldPriceContainer>
              <StrikeThroughTextContainer>
                <OldPriceText h2>£{oldPrice}</OldPriceText>
                <StrikeThrough />
              </StrikeThroughTextContainer>
              <OldPriceText description>per user / month</OldPriceText>
            </OldPriceContainer>
            <Badge badgeType="secondary" fitContent>
              Early access price
            </Badge>
          </Flex>
          <NewPriceContainer>
            <Text h1>£{newPrice}</Text>
            <Text>per user / month</Text>
          </NewPriceContainer>
          <Text>{description}</Text>
        </Section>
        <Section>
          <Button buttonType="secondary" onClick={handleRequestInformation}>
            Request information
          </Button>
        </Section>
      </Flex>
    </Card>
  );
}

const Section = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  color: theme.colors.sysTextDefault,
  width: "fit-content",
}));

const OldPriceContainer = styled(Flex)(({ theme }) => ({
  alignItems: "baseline",
  gap: 4,
  color: theme.colors.sysTextSecondary,
}));

const OldPriceText = styled(Text)({
  opacity: 0.5,
});

const NewPriceContainer = styled(Flex)({
  alignItems: "baseline",
  gap: 4,
});

const StrikeThroughTextContainer = styled.div({
  position: "relative",
});

const StrikeThrough = styled.div(({ theme }) => ({
  position: "absolute",
  top: "70%",
  left: 0,
  width: "100%",
  height: 2,
  backgroundColor: theme.colors.sysDestructiveDefault,
  transform: "rotate(-20deg)",
  transformOrigin: "left center",
}));
